import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { Search, Bot, PenSquare, Minus, RefreshCcw, Check, X, MessageCircle, Inbox, MoreVertical, LogOut } from 'lucide-react';
import { CHAT_TEST_IDS } from '@edusense/shared-constants';
import { Tooltip, Avatar } from '@heroui/react';
const buildParticipantLabel = (participant, fallback)=>{
    if (!participant) {
        return fallback;
    }
    const rawDisplay = typeof participant.displayName === 'string' ? participant.displayName.trim() : '';
    const rawUserName = typeof participant.userName === 'string' ? participant.userName.trim() : typeof participant.name === 'string' ? participant.name.trim() : '';
    if (rawDisplay && rawUserName && rawDisplay !== rawUserName) {
        return `${rawDisplay}@${rawUserName}`;
    }
    if (rawDisplay) {
        return rawDisplay;
    }
    if (rawUserName) {
        return rawUserName;
    }
    const rawId = typeof participant.userId === 'string' ? participant.userId.trim() : typeof participant.id === 'string' ? participant.id.trim() : '';
    if (rawId) {
        return rawId;
    }
    return fallback;
};
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--theme-paper, #ffffff);
  color: var(--theme-text, #111827);

  .dark & {
    background-color: var(--theme-paper, #1f2937);
    color: var(--theme-text, #f3f4f6);
  }
`;
const Header = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--theme-border, #e5e7eb);

  .dark & {
    border-color: var(--theme-border, #374151);
  }
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;
const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  background-color: transparent;
  color: inherit;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--theme-hover, #f3f4f6);
    border-color: var(--theme-border, #e5e7eb);
  }

  .dark & {
    &:hover {
      background-color: var(--theme-hover, #374151);
      border-color: var(--theme-border, #4b5563);
    }
  }
`;
const SearchContainer = styled.div`
  position: relative;
  margin-top: 0.75rem;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.85rem 0.6rem 2.6rem;
  border-radius: 0.5rem;
  border: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-input, #f9fafb);
  font-size: 0.95rem;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--theme-primary, #10b981);
    background-color: var(--theme-paper, #ffffff);
  }

  .dark & {
    background-color: var(--theme-input, #374151);
    border-color: var(--theme-border, #4b5563);

    &:focus {
      background-color: var(--theme-paper, #1f2937);
    }
  }
`;
const SearchIconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 0.85rem;
  transform: translateY(-50%);
  color: var(--theme-text-muted, #9ca3af);
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;
const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${(props)=>props.iconOnly ? '0' : '0.4rem'};
  padding: ${(props)=>props.iconOnly ? '0.45rem' : '0.45rem 0.9rem'};
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  position: relative;
  border: 1px solid var(--theme-border, #e5e7eb);
  background-color: ${({ primary })=>primary ? 'var(--theme-primary, #10b981)' : 'transparent'};
  color: ${({ primary })=>primary ? '#ffffff' : 'inherit'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: ${({ primary })=>primary ? 'var(--theme-primary-dark, #059669)' : 'var(--theme-hover, #f3f4f6)'};
    border-color: var(--theme-border, #d1d5db);
  }

  .dark & {
    border-color: var(--theme-border, #4b5563);

    &:hover:not(:disabled) {
      background-color: ${({ primary })=>primary ? 'var(--theme-primary-dark, #059669)' : 'var(--theme-hover, #374151)'};
      border-color: var(--theme-border, #6b7280);
    }
  }
`;
const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;
const SectionTitle = styled.h2`
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
`;
const PendingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;
const PendingItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--theme-border, #e5e7eb);
  background-color: var(--theme-paper-elevated, #fff);

  .dark & {
    border-color: var(--theme-border, #374151);
    background-color: var(--theme-paper-elevated, #111827);
  }
`;
const PendingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;
const PendingActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const PendingActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 0.45rem;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${({ variant })=>variant === 'primary' ? 'var(--theme-primary, #10b981)' : '#ef4444'};
  color: #ffffff;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ variant })=>variant === 'primary' ? 'var(--theme-primary-dark, #059669)' : '#dc2626'};
  }
`;
const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;
const ChatItemContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--theme-hover, #f3f4f6);
  }

  &:focus-visible {
    outline: 2px solid var(--theme-primary, #3b82f6);
    outline-offset: 2px;
  }

  .dark & {
    &:hover {
      background-color: var(--theme-hover, #374151);
    }
  }
`;
const ChatInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`;
const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;
const ChatName = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ChatMeta = styled.span`
  font-size: 0.75rem;
  color: var(--theme-text-muted, #6b7280);
`;
const ChatPreview = styled.span`
  font-size: 0.82rem;
  color: var(--theme-text-secondary, #4b5563);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);
  }
`;
const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.75rem;
`;
const MoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--theme-text-secondary, #6b7280);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--theme-hover, #f3f4f6);
  }

  .dark & {
    color: var(--theme-text-secondary, #9ca3af);

    &:hover {
      background: var(--theme-hover, #374151);
    }
  }
`;
const ContextMenu = styled.div`
  position: fixed;
  top: ${(props)=>props.$y}px;
  left: ${(props)=>props.$x}px;
  min-width: 180px;
  background: var(--theme-paper, #ffffff);
  border: 1px solid var(--theme-border, #e5e7eb);
  border-radius: 0.5rem;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  padding: 0.35rem 0;
  z-index: 9999;

  .dark & {
    background: var(--theme-paper, #1f2937);
    border-color: var(--theme-border, #374151);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.45);
  }
`;
const ContextMenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: none;
  color: var(--theme-text, #111827);
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--theme-hover, #f3f4f6);
  }

  .dark & {
    color: var(--theme-text, #f9fafb);

    &:hover {
      background: var(--theme-hover, #374151);
    }
  }
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  padding: 3rem 1rem;
  text-align: center;
  color: var(--theme-text-secondary, #6b7280);
`;
const Spinner = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  border: 2px solid var(--theme-border, #d1d5db);
  border-top-color: var(--theme-primary, #10b981);
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const MENU_WIDTH = 220;
const MENU_HEIGHT = 120;
const ChatListView = ({ searchTerm, onSearchChange, chats, pendingRequests, onChatItemClick, onCreateChatClick, onSwitchToAIChat, onClose, onMessageSearchClick, isLoading, isRefreshing, onRefresh, onMarkAllAsRead, onDeleteThread, totalUnreadCount, onAcceptRequest, onDeclineRequest, isPendingLoading })=>{
    // undefined 방어 코드
    const safeChats = chats || [];
    const safePendingRequests = pendingRequests || [];
    const hasChats = safeChats.length > 0;
    const hasPending = safePendingRequests.length > 0;
    // 사이드바 너비 체크 (350px 이하면 아이콘 모드)
    const [isCompactMode, setIsCompactMode] = React.useState(false);
    const [contextMenuState, setContextMenuState] = React.useState({
        isOpen: false,
        x: 0,
        y: 0
    });
    const contextMenuRef = React.useRef(null);
    const closeContextMenu = React.useCallback(()=>{
        setContextMenuState((prev)=>{
            if (!prev.isOpen) {
                return prev;
            }
            return {
                isOpen: false,
                x: 0,
                y: 0,
                roomId: undefined,
                roomTitle: undefined
            };
        });
    }, []);
    const openContextMenu = React.useCallback((x, y, roomId, roomTitle)=>{
        if (typeof window === 'undefined') {
            setContextMenuState({
                isOpen: true,
                x,
                y,
                roomId,
                roomTitle
            });
            return;
        }
        const clampedX = Math.min(Math.max(12, x), Math.max(12, window.innerWidth - MENU_WIDTH - 12));
        const clampedY = Math.min(Math.max(12, y), Math.max(12, window.innerHeight - MENU_HEIGHT - 12));
        setContextMenuState({
            isOpen: true,
            x: clampedX,
            y: clampedY,
            roomId,
            roomTitle
        });
    }, []);
    React.useEffect(()=>{
        const checkSidebarWidth = ()=>{
            const sidebar = document.querySelector('.main-layout__right-sidebar');
            if (sidebar) {
                const width = sidebar.offsetWidth;
                setIsCompactMode(width < 350);
            }
        };
        checkSidebarWidth();
        window.addEventListener('resize', checkSidebarWidth);
        // ResizeObserver로 사이드바 리사이즈 감지
        const sidebar = document.querySelector('.main-layout__right-sidebar');
        let observer = null;
        if (sidebar) {
            observer = new ResizeObserver(checkSidebarWidth);
            observer.observe(sidebar);
        }
        return ()=>{
            window.removeEventListener('resize', checkSidebarWidth);
            if (observer) observer.disconnect();
        };
    }, []);
    React.useEffect(()=>{
        if (!contextMenuState.isOpen) {
            return;
        }
        const handlePointerDown = (event)=>{
            if (contextMenuRef.current?.contains(event.target)) {
                return;
            }
            closeContextMenu();
        };
        const handleKeyDown = (event)=>{
            if (event.key === 'Escape') {
                closeContextMenu();
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('contextmenu', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return ()=>{
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('contextmenu', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        contextMenuState.isOpen,
        closeContextMenu
    ]);
    const renderPendingRequests = ()=>{
        if (isPendingLoading) {
            return /*#__PURE__*/ _jsx(PendingList, {
                children: /*#__PURE__*/ _jsxs(PendingItemContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(PendingInfo, {
                            children: /*#__PURE__*/ _jsx("span", {
                                style: {
                                    fontWeight: 600
                                },
                                children: "채팅 요청을 불러오는 중..."
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spinner, {})
                    ]
                })
            });
        }
        if (!hasPending) {
            return /*#__PURE__*/ _jsx(PendingList, {
                children: /*#__PURE__*/ _jsx(PendingItemContainer, {
                    children: /*#__PURE__*/ _jsx(PendingInfo, {
                        children: /*#__PURE__*/ _jsx("span", {
                            style: {
                                fontSize: '0.85rem',
                                color: 'var(--theme-text-secondary, #6b7280)'
                            },
                            children: "보류 중인 요청이 없습니다."
                        })
                    })
                })
            });
        }
        return /*#__PURE__*/ _jsx(PendingList, {
            children: safePendingRequests.map((request)=>{
                const participants = (request.participants || []).map((p)=>p.displayName || p.userName || p.userId).join(', ');
                const requestedAt = request.currentUserStatus?.requestedAt;
                return /*#__PURE__*/ _jsxs(PendingItemContainer, {
                    children: [
                        /*#__PURE__*/ _jsxs(PendingInfo, {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        fontWeight: 600
                                    },
                                    children: participants || `채팅방 ${request.chatRoomId}`
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        fontSize: '0.8rem',
                                        color: 'var(--theme-text-secondary, #6b7280)'
                                    },
                                    children: requestedAt ? new Date(requestedAt).toLocaleString('ko-KR') : '요청 수신'
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(PendingActions, {
                            children: [
                                /*#__PURE__*/ _jsxs(PendingActionButton, {
                                    variant: "primary",
                                    onClick: ()=>onAcceptRequest?.(String(request.chatRoomId)),
                                    children: [
                                        /*#__PURE__*/ _jsx(Check, {
                                            size: 16
                                        }),
                                        " 수락"
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(PendingActionButton, {
                                    variant: "danger",
                                    onClick: ()=>onDeclineRequest?.(String(request.chatRoomId)),
                                    children: [
                                        /*#__PURE__*/ _jsx(X, {
                                            size: 16
                                        }),
                                        " 거절"
                                    ]
                                })
                            ]
                        })
                    ]
                }, request.chatRoomId);
            })
        });
    };
    const handleChatItemContextMenu = React.useCallback((event, roomId, roomTitle)=>{
        if (!onDeleteThread) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const x = event.clientX;
        const y = event.clientY;
        openContextMenu(x, y, roomId, roomTitle);
    }, [
        onDeleteThread,
        openContextMenu
    ]);
    const handleMoreButtonClick = React.useCallback((event, roomId, roomTitle)=>{
        if (!onDeleteThread) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        const x = rect.right - MENU_WIDTH;
        const y = rect.bottom + 8;
        openContextMenu(x, y, roomId, roomTitle);
    }, [
        onDeleteThread,
        openContextMenu
    ]);
    const handleLeaveThread = React.useCallback(()=>{
        if (!onDeleteThread || !contextMenuState.roomId) {
            closeContextMenu();
            return;
        }
        const targetRoomId = contextMenuState.roomId;
        closeContextMenu();
        onDeleteThread(targetRoomId);
    }, [
        contextMenuState.roomId,
        onDeleteThread,
        closeContextMenu
    ]);
    const renderChatList = ()=>{
        if (isLoading && !hasChats) {
            return /*#__PURE__*/ _jsxs(EmptyState, {
                children: [
                    /*#__PURE__*/ _jsx(Spinner, {}),
                    /*#__PURE__*/ _jsx("span", {
                        children: "채팅 목록을 불러오는 중입니다..."
                    })
                ]
            });
        }
        if (!hasChats) {
            return /*#__PURE__*/ _jsxs(EmptyState, {
                children: [
                    /*#__PURE__*/ _jsx(MessageCircle, {
                        size: 40
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    fontWeight: 600,
                                    marginBottom: '0.4rem'
                                },
                                children: "아직 채팅이 없습니다"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    fontSize: '0.85rem',
                                    color: 'var(--theme-text-secondary, #6b7280)'
                                },
                                children: "새로운 대화를 시작해보세요."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(ToolbarButton, {
                        primary: true,
                        onClick: onCreateChatClick,
                        "data-testid": CHAT_TEST_IDS.LIST_NEW_CHAT_BUTTON,
                        children: [
                            /*#__PURE__*/ _jsx(PenSquare, {
                                size: 16
                            }),
                            " 새 채팅"
                        ]
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsx(ChatList, {
            "data-testid": CHAT_TEST_IDS.LIST_ITEMS_WRAPPER,
            children: safeChats.map((chat, index)=>{
                const chatItemId = String(chat.chatRoomId ?? chat.id ?? chat.roomId ?? chat.room?.id ?? index);
                const lastMessageText = typeof chat.lastMessage === 'string' ? chat.lastMessage : chat.lastMessage?.text || '';
                const participants = Array.isArray(chat?.participants) ? chat.participants : [];
                const otherParticipants = participants.filter((participant)=>{
                    const id = participant?.userId ?? participant?.id;
                    if (!id) {
                        return true;
                    }
                    if (!participant?.isCurrentUserExplicit) {
                        return id !== 'current_user';
                    }
                    return !participant?.isCurrentUserExplicit;
                });
                const primaryParticipant = otherParticipants[0] ?? participants[0];
                const lastMessageTime = typeof chat.lastMessage === 'string' ? chat.lastMessageTime : chat.lastMessage?.timestamp || chat.lastMessageTime;
                const unreadCount = chat.unreadMessageCount ?? chat.unreadCount ?? 0;
                const chatTitle = (()=>{
                    if (typeof chat.roomName === 'string' && chat.roomName.trim().length > 0) {
                        return chat.roomName;
                    }
                    if (typeof chat.displayName === 'string' && chat.displayName.trim().length > 0) {
                        return chat.displayName;
                    }
                    return buildParticipantLabel(primaryParticipant, `채팅방 ${chat.id || chat.chatRoomId}`);
                })();
                const avatarSrc = primaryParticipant?.avatarUrl || primaryParticipant?.avatarImageLink || primaryParticipant?.profileImageUrl || null;
                const isContextMenuOpenForItem = contextMenuState.isOpen && contextMenuState.roomId === chatItemId;
                return /*#__PURE__*/ _jsxs(ChatItemContainer, {
                    role: "button",
                    tabIndex: 0,
                    onClick: ()=>onChatItemClick(chat),
                    onKeyDown: (event)=>{
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onChatItemClick(chat);
                        }
                    },
                    onContextMenu: (event)=>handleChatItemContextMenu(event, chatItemId, chatTitle),
                    "data-testid": `${CHAT_TEST_IDS.ROOM_ITEM_PREFIX}-${chatItemId}`,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            style: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                flex: 1,
                                minWidth: 0
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Avatar, {
                                    size: "md",
                                    src: avatarSrc ?? undefined,
                                    name: chatTitle
                                }),
                                /*#__PURE__*/ _jsxs(ChatInfo, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(ChatTitle, {
                                            children: [
                                                /*#__PURE__*/ _jsx(ChatName, {
                                                    "data-testid": `${CHAT_TEST_IDS.ROOM_NAME_PREFIX}-${chatItemId}`,
                                                    children: chatTitle
                                                }),
                                                /*#__PURE__*/ _jsx(ChatMeta, {
                                                    children: lastMessageTime ? new Date(lastMessageTime).toLocaleTimeString('ko-KR', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : ''
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(ChatPreview, {
                                            "data-testid": `chat-room-preview-${chat.id || chat.chatRoomId || index}`,
                                            children: lastMessageText || '메시지가 없습니다.'
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(ItemActions, {
                            children: [
                                typeof unreadCount === 'number' && unreadCount > 0 && /*#__PURE__*/ _jsx("div", {
                                    "data-testid": `${CHAT_TEST_IDS.ROOM_UNREAD_BADGE_PREFIX}-${chatItemId}`,
                                    style: {
                                        minWidth: '1.75rem',
                                        height: '1.75rem',
                                        borderRadius: '9999px',
                                        backgroundColor: '#3b82f6',
                                        color: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                    },
                                    children: unreadCount
                                }),
                                onDeleteThread && /*#__PURE__*/ _jsx(MoreButton, {
                                    type: "button",
                                    onClick: (event)=>handleMoreButtonClick(event, chatItemId, chatTitle),
                                    "aria-haspopup": "menu",
                                    "aria-expanded": isContextMenuOpenForItem,
                                    "data-testid": `${CHAT_TEST_IDS.ROOM_MORE_BUTTON_PREFIX}-${chatItemId}`,
                                    children: /*#__PURE__*/ _jsx(MoreVertical, {
                                        size: 16
                                    })
                                })
                            ]
                        })
                    ]
                }, chatItemId);
            })
        });
    };
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                children: [
                    /*#__PURE__*/ _jsxs(TitleRow, {
                        children: [
                            /*#__PURE__*/ _jsxs(Title, {
                                children: [
                                    /*#__PURE__*/ _jsx(Inbox, {
                                        size: 20
                                    }),
                                    " 메시지"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(HeaderActions, {
                                children: [
                                    onSwitchToAIChat && (isCompactMode ? /*#__PURE__*/ _jsx(Tooltip, {
                                        content: "AI 튜터",
                                        placement: "bottom",
                                        delay: 0,
                                        closeDelay: 0,
                                        className: "px-2 py-1 text-xs",
                                        children: /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: onSwitchToAIChat,
                                            iconOnly: true,
                                            children: /*#__PURE__*/ _jsx(Bot, {
                                                size: 16
                                            })
                                        })
                                    }) : /*#__PURE__*/ _jsxs(ToolbarButton, {
                                        onClick: onSwitchToAIChat,
                                        iconOnly: false,
                                        children: [
                                            /*#__PURE__*/ _jsx(Bot, {
                                                size: 16
                                            }),
                                            " AI 튜터"
                                        ]
                                    })),
                                    isCompactMode ? /*#__PURE__*/ _jsx(Tooltip, {
                                        content: "새 채팅",
                                        placement: "bottom",
                                        delay: 0,
                                        closeDelay: 0,
                                        className: "px-2 py-1 text-xs",
                                        children: /*#__PURE__*/ _jsx(ToolbarButton, {
                                            onClick: onCreateChatClick,
                                            "data-testid": CHAT_TEST_IDS.LIST_NEW_CHAT_BUTTON,
                                            iconOnly: true,
                                            children: /*#__PURE__*/ _jsx(PenSquare, {
                                                size: 16
                                            })
                                        })
                                    }) : /*#__PURE__*/ _jsxs(ToolbarButton, {
                                        onClick: onCreateChatClick,
                                        "data-testid": CHAT_TEST_IDS.LIST_NEW_CHAT_BUTTON,
                                        iconOnly: false,
                                        children: [
                                            /*#__PURE__*/ _jsx(PenSquare, {
                                                size: 16
                                            }),
                                            " 새 채팅"
                                        ]
                                    }),
                                    onClose && /*#__PURE__*/ _jsx(IconButton, {
                                        onClick: onClose,
                                        children: /*#__PURE__*/ _jsx(Minus, {
                                            size: 18
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Toolbar, {
                        children: [
                            onRefresh && /*#__PURE__*/ _jsxs(ToolbarButton, {
                                onClick: onRefresh,
                                disabled: isRefreshing,
                                children: [
                                    isRefreshing ? /*#__PURE__*/ _jsx(Spinner, {}) : /*#__PURE__*/ _jsx(RefreshCcw, {
                                        size: 16
                                    }),
                                    " 새로고침"
                                ]
                            }),
                            onMessageSearchClick && /*#__PURE__*/ _jsxs(ToolbarButton, {
                                onClick: onMessageSearchClick,
                                children: [
                                    /*#__PURE__*/ _jsx(Search, {
                                        size: 16
                                    }),
                                    " 메시지 검색"
                                ]
                            }),
                            onMarkAllAsRead && /*#__PURE__*/ _jsx(ToolbarButton, {
                                onClick: onMarkAllAsRead,
                                disabled: !totalUnreadCount,
                                children: "모두 읽음"
                            }),
                            typeof totalUnreadCount === 'number' && totalUnreadCount > 0 && /*#__PURE__*/ _jsxs("span", {
                                style: {
                                    fontSize: '0.8rem',
                                    color: 'var(--theme-text-secondary, #6b7280)'
                                },
                                children: [
                                    "읽지 않은 메시지 ",
                                    totalUnreadCount,
                                    "개"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SearchContainer, {
                        children: [
                            /*#__PURE__*/ _jsx(SearchIconWrapper, {
                                children: /*#__PURE__*/ _jsx(Search, {
                                    size: 18
                                })
                            }),
                            /*#__PURE__*/ _jsx(SearchInput, {
                                placeholder: "참여자 이름이나 메시지로 검색",
                                value: searchTerm,
                                onChange: (event)=>onSearchChange(event.target.value),
                                "data-testid": CHAT_TEST_IDS.LIST_SEARCH_INPUT
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Content, {
                children: [
                    /*#__PURE__*/ _jsxs(Section, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionHeader, {
                                children: /*#__PURE__*/ _jsxs(SectionTitle, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Inbox, {
                                            size: 18
                                        }),
                                        " 요청함"
                                    ]
                                })
                            }),
                            renderPendingRequests()
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Section, {
                        "data-testid": CHAT_TEST_IDS.LIST_CONTAINER,
                        children: [
                            /*#__PURE__*/ _jsx(SectionHeader, {
                                children: /*#__PURE__*/ _jsxs(SectionTitle, {
                                    children: [
                                        /*#__PURE__*/ _jsx(MessageCircle, {
                                            size: 18
                                        }),
                                        " 최근 대화"
                                    ]
                                })
                            }),
                            renderChatList()
                        ]
                    })
                ]
            }),
            contextMenuState.isOpen && onDeleteThread && contextMenuState.roomId && /*#__PURE__*/ _jsx(ContextMenu, {
                ref: contextMenuRef,
                $x: contextMenuState.x,
                $y: contextMenuState.y,
                role: "menu",
                "data-testid": `${CHAT_TEST_IDS.ROOM_CONTEXT_MENU}-${contextMenuState.roomId}`,
                children: /*#__PURE__*/ _jsxs(ContextMenuItem, {
                    role: "menuitem",
                    onClick: handleLeaveThread,
                    "data-testid": `${CHAT_TEST_IDS.ROOM_MENU_LEAVE_BUTTON}-${contextMenuState.roomId}`,
                    children: [
                        /*#__PURE__*/ _jsx(LogOut, {
                            size: 16
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            style: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.1rem',
                                alignItems: 'flex-start'
                            },
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: "채팅 나가기"
                                }),
                                contextMenuState.roomTitle && /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        fontSize: '0.75rem',
                                        color: 'var(--theme-text-secondary, #6b7280)'
                                    },
                                    children: contextMenuState.roomTitle
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
export default ChatListView;
